import { contactsService } from "@/services/contactsService";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import type { ContactSummary } from "@/types/entities";

const CACHE_TTL_MS = 60_000;
/** Page size for GET /contacts?search=… uniqueness lookups. */
export const UNIQUENESS_SEARCH_PAGE_SIZE = 20;

export interface ContactSearchScope {
  legalEntityId?: string;
  branchId?: string;
}

interface CacheEntry {
  at: number;
  content: ContactSummary[];
}

const resultCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<ContactSummary[]>>();

function cacheKey(query: string, scope?: ContactSearchScope): string {
  const branch = scope?.branchId?.trim() ?? "";
  const entity = scope?.legalEntityId?.trim() ?? "";
  return `${entity}|${branch}|${query.trim().toLowerCase()}`;
}

export function clearContactSearchCache(): void {
  resultCache.clear();
}

/** GET /contacts?search=…&page=0&size=20 — deduped in-flight and cached. */
export function fetchContactsBySearch(
  query: string,
  options?: { signal?: AbortSignal; scope?: ContactSearchScope },
): Promise<ContactSummary[]> {
  const trimmed = query.trim();
  if (!trimmed) return Promise.resolve([]);

  const key = cacheKey(trimmed, options?.scope);

  if (!options?.signal?.aborted) {
    const cached = resultCache.get(key);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      return Promise.resolve(cached.content);
    }
  }

  const pending = inFlight.get(key);
  if (pending && !options?.signal?.aborted) {
    return pending;
  }

  const listScope = contactPropertyListScope(
    options?.scope?.legalEntityId,
    options?.scope?.branchId,
  );

  const promise = contactsService
    .list({ ...listScope, search: trimmed }, { page: 0, size: UNIQUENESS_SEARCH_PAGE_SIZE }, {
      signal: options?.signal,
    })
    .then((res) => {
      if (options?.signal?.aborted) return [];
      const content = res.content ?? [];
      resultCache.set(key, { at: Date.now(), content });
      return content;
    })
    .catch((err) => {
      if (options?.signal?.aborted) return [];
      resultCache.delete(key);
      throw err;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
}
